// hooks/useChangeTracking.js
import { useEffect, useRef, useState, useCallback } from 'react';
import { isEqual, debounce } from 'lodash';

export const useChangeTracking = (data, dependencies = []) => {
    const [hasChanges, setHasChanges] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const initialDataRef = useRef(null);
    const previousDataRef = useRef(null);

    // Initialize tracking
    useEffect(() => {
        if (!isInitialized && data && Object.keys(data).length > 0) {
            // Deep clone the initial data
            initialDataRef.current = JSON.parse(JSON.stringify(data));
            previousDataRef.current = JSON.parse(JSON.stringify(data));
            setIsInitialized(true);
            setHasChanges(false);
        }
    }, [data, isInitialized]);

    // Debounced comparison function
    const checkForChanges = useCallback(
        debounce(() => {
            if (!isInitialized || !initialDataRef.current) return;

            try {
                // Deep comparison between current and initial data
                const hasDataChanged = !isEqual(data, initialDataRef.current);

                if (hasDataChanged !== hasChanges) {
                    setHasChanges(hasDataChanged);
                }

                // Update previous data reference
                previousDataRef.current = JSON.parse(JSON.stringify(data));
            } catch (error) {
                console.error('Error in change tracking:', error);
            }
        }, 300),
        [data, hasChanges, isInitialized]
    );

    // Track changes
    useEffect(() => {
        if (isInitialized) {
            checkForChanges();
        }

        // Cleanup
        return () => {
            checkForChanges.cancel();
        };
    }, [data, checkForChanges, isInitialized, ...dependencies]);

    // Reset tracking - updates the initial reference to current state
    const resetTracking = useCallback(() => {
        if (data) {
            initialDataRef.current = JSON.parse(JSON.stringify(data));
            previousDataRef.current = JSON.parse(JSON.stringify(data));
            setHasChanges(false);
        }
    }, [data]);

    // Get list of changed fields with their old and new values
    const getChangedFields = useCallback(() => {
        if (!initialDataRef.current || !data) return [];

        const changes = [];

        const compareObjects = (obj1, obj2, path = '') => {
            // Get all keys from both objects
            const allKeys = new Set([
                ...Object.keys(obj1 || {}),
                ...Object.keys(obj2 || {})
            ]);

            for (const key of allKeys) {
                const newPath = path ? `${path}.${key}` : key;
                const value1 = obj1?.[key];
                const value2 = obj2?.[key];

                if (typeof value1 === 'object' && value1 !== null &&
                    typeof value2 === 'object' && value2 !== null &&
                    !Array.isArray(value1) && !Array.isArray(value2) &&
                    !(value1 instanceof Date) && !(value2 instanceof Date)) {
                    // Recursively compare nested objects
                    compareObjects(value1, value2, newPath);
                } else if (!isEqual(value1, value2)) {
                    changes.push({
                        field: newPath,
                        oldValue: value1,
                        newValue: value2,
                        type: getChangeType(value1, value2)
                    });
                }
            }
        };

        compareObjects(initialDataRef.current, data);
        return changes;
    }, [data]);

    // Determine the type of change
    const getChangeType = (oldValue, newValue) => {
        if (oldValue === undefined || oldValue === null || oldValue === '') {
            return 'added';
        } else if (newValue === undefined || newValue === null || newValue === '') {
            return 'removed';
        } else {
            return 'modified';
        }
    };

    // Check if a specific field has changed
    const hasFieldChanged = useCallback((fieldPath) => {
        if (!initialDataRef.current || !data) return false;

        const getNestedValue = (obj, path) => {
            return path.split('.').reduce((current, key) => current?.[key], obj);
        };

        const initialValue = getNestedValue(initialDataRef.current, fieldPath);
        const currentValue = getNestedValue(data, fieldPath);

        return !isEqual(initialValue, currentValue);
    }, [data]);

    // Get the previous value of a field
    const getPreviousValue = useCallback((fieldPath) => {
        if (!previousDataRef.current) return undefined;

        return fieldPath.split('.').reduce(
            (current, key) => current?.[key],
            previousDataRef.current
        );
    }, []);

    return {
        hasChanges,
        isInitialized,
        resetTracking,
        getChangedFields,
        hasFieldChanged,
        getPreviousValue
    };
};